import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import Stats from "./components/Stats";
import Filters from "./components/Filters";
import ControlRow from "./components/ControlRow";
import TradingTable from "./components/TradingTable";
import TraderOfTheDay from "./components/TraderOfTheDay";
import PromoSection from "./components/PromoSection";
import Footer from "./components/Footer";
import BitcoinLogo from "./components/BitcoinLogo";
import BackToTop from "./components/BackToTop";
import Loading from "./components/Loading";
import Notification from "./components/Notification";
import ActivityPopup from "./components/ActivityPopup";
import RuneMarketPopup from "./components/RuneMarketPopup.js"; // New import
import { getSwapTotalData, getSwapHistoryData, getSwapHistoryDataByAddress } from "./utils/api";

const SATOSHIS_PER_BTC = 1;

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    message: "",
    show: false,
  });
  const [addressData, setAddressData] = useState({});
  const [topData, setTopData] = useState({});
  const [marketPrices, setMarketPrices] = useState({});
  const [historyData, setHistoryData] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    minTrades: 0,
    minProfit: -10,
    minVolume: 0,
  });
  const [sort, setSort] = useState({ column: "profit", order: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);
  const [popup, setPopup] = useState({ type: null, data: null });

  const [partialLoading, setPartialLoading] = useState(false);

  // Fetch and process all trade data
  const fetchAllTradeData = async () => {
    setLoading(true);
    try {
      const totalSwapData = await getSwapTotalData();
      setAddressData(totalSwapData.data);

    } catch (error) {
      console.error("Detailed error fetching trade data:", error);
      setAddressData({});
      showNotification(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async (currentPage) => {
    try {
      setPartialLoading(true);
      const historyData = await getSwapHistoryData(filters.search, filters.minTrades, filters.minProfit, parseInt(filters.minVolume * Math.pow(10, 8)), sort.column, sort.order, limit, (currentPage - 1) * limit);
      setTotalPages(Math.ceil(historyData.total / limit));
      setHistoryData(historyData.data);
      setPartialLoading(false);
    }
    catch (error) {
      console.error("Error fetching history data:", error);
    }
  }

  useEffect(() => {
    fetchHistoryData(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchHistoryData(1);
      setCurrentPage(1);
    }, 500); // Debounce time of 300 milliseconds
    return () => clearTimeout(debounceTimer);
  }, [filters, sort]);
  
  useEffect(() => {
    fetchAllTradeData();
  }, []);

  const showNotification = (message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: "", show: false }), 2000);
  };

  const exportData = () => {
    const headers = [
      "Address",
      "Profit %",
      "APR",
      "30-day Profit %",
      "Trades",
      "Total Volume (BTC)",
    ];
    const rows = historyData.map((data) => [
      data.address,
      data.profit,
      data.apr,
      data.profit30d !== null ? data['30dProfit'] : "N/A",
      data.trades,
      (data.totalBtcAmount / Math.pow(10, 8)).toFixed(5),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "universe_leaderboard.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-gray-800 text-white" : "bg-gray-900 text-gray-200"
      } transition-colors duration-300 relative`}
    >
      <BitcoinLogo />
      <Header darkMode={darkMode} setDarkMode={setDarkMode} />
      <BackToTop />
      {loading && <Loading />}

      {partialLoading && (
        <div className="absolute inset-0 flex justify-center items-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 rounded-full border-t-[#F7007B]" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      )}

      <Notification message={notification.message} show={notification.show} />
      <main className="container mx-auto px-4 py-8">
        <Stats data={addressData} />
        <Filters filters={filters} setFilters={setFilters} />
        <ControlRow
          filters={filters}
          setFilters={setFilters}
          refreshData={fetchAllTradeData}
          exportData={exportData}
        />

        <div className="flex flex-col lg:flex-row gap-6">
          <TradingTable
            data={historyData}
            filters={filters}
            sort={sort}
            setSort={setSort}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            showNotification={showNotification}
            setPopup={setPopup}
            totalPages={totalPages}
            setPartialLoading={setPartialLoading}
          />
          <TraderOfTheDay
            data={addressData}
            setPopup={setPopup}
            showNotification={showNotification}
          />
        </div>
        <PromoSection />
      </main>
      {popup.type === "trades" && (
        <TradesPopup
          data={popup.data}
          marketPrices={marketPrices}
          closePopup={() => setPopup({ type: null, data: null })}
          showNotification={showNotification}
        />
      )}
      {popup.type === "chart" && (
        <ActivityPopup
          data={popup.data}
          closePopup={() => setPopup({ type: null, data: null })}
        />
      )}
      {popup.type === "runeMarket" && (
        <RuneMarketPopup
          data={popup.data}
          closePopup={() => setPopup({ type: null, data: null })}
        />
      )}
      <Footer />
    </div>
  );
}

export default App;
